import React, { useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cpu, WandSparkles, TrendingUp, Award, ShieldCheck, Zap, Crown, DollarSign } from "lucide-react";
import TestimonialCard from "@/components/TestimonialCard";
import PricingCard from "@/components/PricingCard";
import PlanosCardsTon from "@/components/home/PlanosCardsTon";
import { AffiliateReferralBanner } from "@/components/affiliate/AffiliateReferralBanner";
import { useAuth } from "@/context/AuthContext";

const HomePage = () => {
  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "IA Palpite Guru",
      alternateName: "AI Palpite Guru",
      url: "https://aipalpiteguru.mentepoderosa2030.com",
      inLanguage: "pt-BR",
      potentialAction: {
        "@type": "SearchAction",
        target:
          "https://aipalpiteguru.mentepoderosa2030.com/estatisticas?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://aipalpiteguru.mentepoderosa2030.com/",
        },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="IA Palpite Guru - Palpites Jogo do Bicho e Loterias com Inteligência Artificial"
        description="IA Palpite Guru: sistema de Inteligência Artificial para palpites do Jogo do Bicho e Loterias Caixa. Métodos exclusivos (Cruz do Dia, 777, Chave de Ouro), resultados Deu no Poste e estatísticas em tempo real."
        keywords="ia palpite guru, ai palpite guru, palpite guru, jogo do bicho, palpites jogo do bicho, resultado jogo do bicho, deu no poste, palpite bicho hoje, bicho que vai dar hoje, previsão jogo do bicho, inteligência artificial loterias, IA palpites, app jogo do bicho"
        canonical="/"
        jsonLd={homeJsonLd}
      />
      <HomePageContent />
    </>
  );
};

const HomePageContent = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, profileLoaded } = useAuth();

  // VERIFICAÇÃO CRÍTICA: Redireciona usuários FREE logados para /planos
  useEffect(() => {
    if (!isLoading && profileLoaded && isAuthenticated && user) {
      console.log("🏠 HomePage - Verificando usuário logado:", {
        plan: user.plan,
        isSubscribed: user.isSubscribed,
        role: user.role
      });

      const isFreeUser = user.role !== "admin" && (user.plan === "free" || !user.isSubscribed);
      
      if (isFreeUser) {
        console.log("🚫 HomePage - Usuário FREE detectado, redirecionando para /planos");
        navigate("/planos", { replace: true });
      }
    }
  }, [user, isAuthenticated, isLoading, profileLoaded, navigate]);

  return (
    <div className="w-full">
      {/* Hero Section - Full Width */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 w-full">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-purple-900/20 via-slate-900/40 to-slate-900"></div>
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-4 h-4 bg-purple-500 rounded-full animate-pulse opacity-70"></div>
        <div className="absolute top-40 right-20 w-6 h-6 bg-blue-500 rounded-full animate-pulse opacity-50"></div>
        <div className="absolute bottom-32 left-20 w-3 h-3 bg-indigo-500 rounded-full animate-pulse opacity-60"></div>
        
        <div className="w-full max-w-7xl mx-auto px-4 relative z-10">
          <AffiliateReferralBanner />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm font-medium mb-6 backdrop-blur-sm">
                <Cpu className="w-4 h-4 mr-2" />
                Tecnologia de ponta com IA
                <div className="mx-3 w-px h-4 bg-purple-500/30"></div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-green-400 font-semibold">IA ATIVA</span>
                </div>
                <Link to="/planos" className="ml-3">
                  <Button size="sm" className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-3 py-1 text-xs font-bold shadow-lg hover:shadow-yellow-500/25 transition-all duration-300 animate-pulse">
                    <Crown className="w-3 h-3 mr-1" />
                    ACESSO VIP
                  </Button>
                </Link>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white">
                IA PALPITE GURU{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400">
                  I.A do JOGO DO BICHO E LOTERIAS DA CAIXA
                </span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                Maximize suas chances com algoritmos avançados de IA que analisam padrões e tendências para gerar palpites mais precisos no jogo do bicho.
              </p>

              {/* Vídeo (mobile) abaixo da headline */}
              <div className="relative z-30 mb-6 block lg:hidden">
                <div className="bg-slate-800/90 backdrop-blur-xl border border-slate-700/50 rounded-2xl overflow-hidden shadow-2xl">
                  <div className="aspect-video w-full">
                    <iframe
                      className="w-full h-full"
                      src="https://www.youtube-nocookie.com/embed/KJsnankSfzs?rel=0&modestbranding=1"
                      title="Apresentação - IA Palpite Guru"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/pagamento">
                  <Button size="lg" className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-3 text-lg font-semibold shadow-lg hover:shadow-purple-500/25 transition-all duration-300">
                    Começar agora
                  </Button>
                </Link>
                <Link to="/gerar-jogos">
                  <Button size="lg" variant="outline" className="border-purple-400 text-purple-300 hover:bg-purple-500/10 px-8 py-3 text-lg backdrop-blur-sm">
                    Ver palpites
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right Column - Robot and Demo */}
            <div className="relative">
              {/* Main Robot - Novo robô sem fundo */}
              <div className="relative z-20 flex justify-center mb-8">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/30 to-indigo-500/30 rounded-full blur-2xl scale-150 animate-pulse-slow"></div>
                  <img 
                    src="/lovable-uploads/171a312c-a500-4536-be39-bef4d642eb7c.png" 
                    alt="AI Robot" 
                    className="w-64 h-64 lg:w-80 lg:h-80 object-contain relative z-10 drop-shadow-2xl"
                    fetchPriority="high"
                  />
                </div>
              </div>
              
              {/* Vídeo de Apresentação */}
              <div className="relative z-30 mb-8 hidden lg:block">
                <div className="bg-slate-800/90 backdrop-blur-xl border border-slate-700/50 rounded-2xl overflow-hidden shadow-2xl">
                  <div className="aspect-video w-full">
                    <iframe
                      className="w-full h-full"
                      src="https://www.youtube-nocookie.com/embed/KJsnankSfzs?rel=0&modestbranding=1"
                      title="Apresentação - IA Palpite Guru"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  </div>
                </div>
              </div>
              
              {/* Demo Card */}
              <div className="relative z-30 bg-slate-800/90 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 shadow-2xl">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-bold text-xl text-white">Palpites do dia</h3>
                  <div className="bg-purple-500/20 text-purple-300 text-xs px-3 py-1 rounded-full border border-purple-500/30">
                    IA Ativa
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-slate-400 mb-3">Milhar (Amostra)</h4>
                    <div className="grid grid-cols-5 gap-2">
                      <div className="bg-slate-700/50 border border-slate-600/50 rounded-lg p-3 text-center backdrop-blur-sm">
                        <span className="prediction-number text-purple-400">1285</span>
                      </div>
                      <div className="bg-slate-700/50 border border-slate-600/50 rounded-lg p-3 text-center backdrop-blur-sm">
                        <span className="prediction-number text-purple-400">3972</span>
                      </div>
                      <div className="bg-slate-800/50 border border-slate-600/30 rounded-lg p-3 text-center">
                        <span className="prediction-number text-slate-500">????</span>
                      </div>
                      <div className="bg-slate-800/50 border border-slate-600/30 rounded-lg p-3 text-center">
                        <span className="prediction-number text-slate-500">????</span>
                      </div>
                      <div className="bg-slate-800/50 border border-slate-600/30 rounded-lg p-3 text-center">
                        <span className="prediction-number text-slate-500">????</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-slate-400 mb-3">Centena (Amostra)</h4>
                    <div className="grid grid-cols-5 gap-2">
                      <div className="bg-slate-700/50 border border-slate-600/50 rounded-lg p-3 text-center backdrop-blur-sm">
                        <span className="prediction-number text-purple-400">526</span>
                      </div>
                      <div className="bg-slate-700/50 border border-slate-600/50 rounded-lg p-3 text-center backdrop-blur-sm">
                        <span className="prediction-number text-purple-400">198</span>
                      </div>
                      <div className="bg-slate-800/50 border border-slate-600/30 rounded-lg p-3 text-center">
                        <span className="prediction-number text-slate-500">???</span>
                      </div>
                      <div className="bg-slate-800/50 border border-slate-600/30 rounded-lg p-3 text-center">
                        <span className="prediction-number text-slate-500">???</span>
                      </div>
                      <div className="bg-slate-800/50 border border-slate-600/30 rounded-lg p-3 text-center">
                        <span className="prediction-number text-slate-500">???</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-800/50 to-slate-800/90 flex items-end justify-center pb-6 rounded-lg">
                      <Link to="/gerar-jogos">
                        <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg">
                          Ver todos os palpites
                        </Button>
                      </Link>
                    </div>
                    
                    <h4 className="text-sm font-medium text-slate-400 mb-3">Grupo (Premium)</h4>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="bg-slate-800/50 border border-slate-600/30 rounded-lg p-3 text-center">
                        <span className="prediction-number text-slate-500">??</span>
                      </div>
                      <div className="bg-slate-800/50 border border-slate-600/30 rounded-lg p-3 text-center">
                        <span className="prediction-number text-slate-500">??</span>
                      </div>
                      <div className="bg-slate-800/50 border border-slate-600/30 rounded-lg p-3 text-center">
                        <span className="prediction-number text-slate-500">??</span>
                      </div>
                      <div className="bg-slate-800/50 border border-slate-600/30 rounded-lg p-3 text-center">
                        <span className="prediction-number text-slate-500">??</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-50 dark:bg-slate-900/50 w-full">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white">
              Recursos <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600">Exclusivos</span>
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Nossa plataforma utiliza tecnologia de ponta para oferecer palpites de alta precisão com inteligência artificial avançada.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group bg-white dark:bg-slate-800/50 p-8 rounded-2xl border border-slate-200 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <WandSparkles size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-slate-900 dark:text-white">Palpites com IA</h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Algoritmos de inteligência artificial analisam milhares de resultados anteriores para gerar palpites precisos e estratégicos.
              </p>
            </div>
            
            <div className="group bg-white dark:bg-slate-800/50 p-8 rounded-2xl border border-slate-200 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Cpu size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-slate-900 dark:text-white">Software Avançado</h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Ferramenta poderosa para análise de resultados e identificação de padrões complexos no jogo do bicho.
              </p>
            </div>
            
            <div className="group bg-white dark:bg-slate-800/50 p-8 rounded-2xl border border-slate-200 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <TrendingUp size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-slate-900 dark:text-white">Análise de Tendências</h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Visualize tendências e padrões históricos para tomar decisões mais informadas e estratégicas.
              </p>
            </div>
            
            <div className="group bg-white dark:bg-slate-800/50 p-8 rounded-2xl border border-slate-200 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Award size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-slate-900 dark:text-white">Resultados Comprovados</h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Taxa de acerto superior ao método tradicional, comprovada por nossa crescente base de usuários satisfeitos.
              </p>
            </div>
            
            <div className="group bg-white dark:bg-slate-800/50 p-8 rounded-2xl border border-slate-200 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <ShieldCheck size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-slate-900 dark:text-white">Acesso Seguro</h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Informações e palpites protegidos com segurança de ponta, acessíveis 24/7 de forma totalmente segura.
              </p>
            </div>
            
            <div className="group bg-white dark:bg-slate-800/50 p-8 rounded-2xl border border-slate-200 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Zap size={28} className="text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-4 text-slate-900 dark:text-white">Atualizações Constantes</h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Sistema atualizado diariamente com os últimos resultados e melhorias contínuas nos algoritmos de IA.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Affiliate Program Section */}
      <section className="py-24 bg-gradient-to-r from-green-600 to-emerald-600 w-full">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <DollarSign size={32} className="text-white" />
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Programa de Afiliados
            </h2>
            <p className="text-xl text-green-100 max-w-3xl mx-auto mb-8 leading-relaxed">
              Ganhe 20% de comissão em cada venda! Compartilhe nossa plataforma e seja recompensado pelo sucesso dos seus indicados.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white">
                <div className="text-3xl font-bold mb-2">20%</div>
                <div className="text-green-100">Comissão por venda</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white">
                <div className="text-3xl font-bold mb-2">Mensal</div>
                <div className="text-green-100">Pagamentos automáticos</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white">
                <div className="text-3xl font-bold mb-2">24/7</div>
                <div className="text-green-100">Suporte dedicado</div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/afiliados">
                <Button size="lg" className="bg-white text-green-600 hover:bg-gray-100 px-8 py-3 text-lg font-semibold">
                  Quero ser Afiliado
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-3 text-lg">
                Saiba mais
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white dark:bg-slate-800/30 w-full">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">O que nossos usuários dizem</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Descubra como nossa plataforma tem ajudado jogadores a melhorar seus resultados.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <TestimonialCard 
              quote="Depois que comecei a usar o BiChoIA, minha taxa de acertos aumentou consideravelmente. O plano premium vale cada centavo!"
              author="Roberto Silva"
              role="Usuário há 8 meses"
            />
            
            <TestimonialCard 
              quote="Os palpites são incrivelmente precisos. Já recuperei o valor da assinatura várias vezes com os acertos que tive."
              author="Maria Oliveira"
              role="Usuária Premium"
            />
            
            <TestimonialCard 
              quote="O software de análise me ajuda a entender melhor os padrões do jogo. Uma ferramenta indispensável para quem leva o jogo a sério."
              author="Carlos Mendes"
              role="Usuário VIP"
            />
          </div>
        </div>
      </section>

      {/* Planos com pagamento direto via Ton (PIX / Cartão) */}
      <section className="py-20 bg-gradient-to-b from-background to-slate-50 dark:to-slate-900/40 w-full" id="planos-ton">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Assine agora com PIX ou Cartão
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Pagamento processado pela Ton 💳 — acesso liberado após confirmação.
            </p>
          </div>
          <PlanosCardsTon origin="home" page="/" />
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-slate-50 dark:bg-slate-900/50 w-full" id="planos">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Escolha seu plano</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Oferecemos planos flexíveis para atender às suas necessidades.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <PricingCard 
              title="Palpite Guru Mensal"
              price="R$ 44,90"
              description="Acesso completo por 30 dias"
              buttonText="Assinar plano"
              planType="basic"
              usePaymentFlow={true}
              features={[
                { title: "Acesso a todos os palpites premium", included: true },
                { title: "10 Milhar, 10 centena, 4 grupo, 10 duque, 10 terno", included: true },
                { title: "Geração ilimitada com IA", included: true },
                { title: "Acesso por 30 dias", included: true },
                { title: "Suporte básico", included: true },
                { title: "Prioridade em atualizações", included: false },
              ]}
            />
            
            <PricingCard 
              title="Palpite Guru Trimestral"
              price="R$ 59,90"
              description="Economize 15% no plano trimestral"
              buttonText="Assinar plano"
              planType="premium"
              usePaymentFlow={true}
              isPopular={true}
              features={[
                { title: "Acesso a todos os palpites premium", included: true },
                { title: "10 Milhar, 10 centena, 4 grupo, 10 duque, 10 terno", included: true },
                { title: "Geração ilimitada com IA", included: true },
                { title: "Acesso por 3 meses", included: true },
                { title: "15% de economia", included: true },
                { title: "Suporte básico", included: true },
                { title: "Prioridade em atualizações", included: false },
              ]}
            />
            
            <PricingCard 
              title="Palpite Guru Anual"
              price="R$ 189,90"
              description="Melhor custo-benefício no plano anual"
              buttonText="Assinar plano"
              planType="vip"
              usePaymentFlow={true}
              features={[
                { title: "Acesso a todos os palpites premium", included: true },
                { title: "10 Milhar, 10 centena, 4 grupo, 10 duque, 10 terno", included: true },
                { title: "Geração ilimitada com IA", included: true },
                { title: "Acesso por 12 meses", included: true },
                { title: "40% de economia", included: true },
                { title: "Suporte premium", included: true },
                { title: "Prioridade em atualizações", included: true },
              ]}
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden w-full">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-12 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-600/20 via-indigo-600/20 to-blue-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            
            <div className="relative z-10 text-center max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Pronto para transformar seu{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">
                  jogo?
                </span>
              </h2>
              <p className="text-xl text-slate-300 mb-10 leading-relaxed">
                Junte-se a milhares de jogadores que já estão utilizando nossa plataforma de IA para obter melhores resultados no jogo do bicho.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link to="/cadastro">
                  <Button size="lg" className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-10 py-4 text-lg font-semibold shadow-xl hover:shadow-purple-500/25 transition-all duration-300">
                    Experimente grátis
                  </Button>
                </Link>
                <Link to="/gerar-jogos">
                  <Button size="lg" variant="outline" className="border-purple-400 text-purple-300 hover:bg-purple-500/10 px-10 py-4 text-lg backdrop-blur-sm">
                    Ver palpites
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;